import deepmerge from 'deepmerge';
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { ScatterPlotDatum, ScatterPlotOptions } from '@/views/components/charts/scatter-plot/ScatterPlot';
import { get } from 'lodash';
import { monthsToPeriod } from '@/utils/time';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { CURRENCY, currencyToText } from '@/utils/currency';
import { getLatestDates } from '@/utils/comparison';
import ComparisonItem from '@/types/comparison-item';
import { getSharedTooltip } from '@/views/components/charts/utils/tooltip';
import { scatterPlotTooltipFormatter } from '@/views/components/charts/scatter-plot/utils/tooltip';

const NUM_Y_CELLS = 5;
const MIN_RANGE = 10;
const CHART_PADDING_RATIO = 0.1;

const calculateAxisBounds = (data: ScatterPlotDatum[], path: string, minRange: number = MIN_RANGE) => {
  const reducedValues = data.reduce(
    (values: { min: number; max: number }, datum: ScatterPlotDatum) => {
      if (!datum || !datum.data || !datum.data[0]) return values;
      const pathValue = get(datum.data[0], path);
      if (pathValue == null) return values;
      return {
        min: Math.min(pathValue, values.min),
        max: Math.max(pathValue, values.max),
      };
    },
    { min: Infinity, max: -Infinity },
  );

  const range = reducedValues.max - reducedValues.min;
  if (minRange != null && range < minRange) {
    const mean = (reducedValues.max + reducedValues.min) / 2;
    return {
      min: mean - minRange / 2,
      max: mean + minRange / 2,
    };
  }

  const absolutePadding = (reducedValues.max - reducedValues.min) * CHART_PADDING_RATIO;
  return {
    min: reducedValues.min - absolutePadding,
    max: reducedValues.max + absolutePadding,
  };
};

export function calculateRiskRewardAxes(data: ScatterPlotDatum[], width: number, height: number) {
  const xAxis = calculateAxisBounds(data, 'x', MIN_RANGE);
  const yAxis = calculateAxisBounds(data, 'y', MIN_RANGE);

  const xRange = xAxis.max - xAxis.min;
  const yRange = yAxis.max - yAxis.min;

  let axes;

  if (xRange / yRange > width / height) {
    // X-axis is favored
    const tickPixelInterval = height / (NUM_Y_CELLS - 1);
    const tickInterval = Math.ceil((tickPixelInterval / width) * xRange);
    const newYRange = (height / tickPixelInterval) * tickInterval;
    yAxis.min -= (newYRange - yRange) / 2;

    axes = {
      x: {
        min: xAxis.min,
        max: xAxis.max,
        tickInterval,
        tickPixelInterval,
      },
      y: {
        min: yAxis.min,
        max: yAxis.min + newYRange,
        tickInterval,
        tickPixelInterval,
      },
    };
  } else {
    // Y-axis is favored
    const tickInterval = Math.ceil(yRange / (NUM_Y_CELLS - 1));
    const tickPixelInterval = height / (yRange / tickInterval);
    const newXRange = (width / tickPixelInterval) * tickInterval;
    xAxis.min -= (newXRange - xRange) / 2;

    axes = {
      x: {
        min: xAxis.min,
        max: xAxis.min + newXRange,
        tickInterval,
        tickPixelInterval,
      },
      y: {
        min: yAxis.min,
        max: yAxis.max,
        tickInterval,
        tickPixelInterval,
      },
    };
  }

  return axes;
}

export function getRiskRewardChartOptions(axes?: { x: any; y: any }, height?: number, baseOptions?: Partial<ScatterPlotOptions>): ScatterPlotOptions {
  return deepmerge<ScatterPlotOptions>(baseOptions || {}, {
    chart: {
      height,
    },
    tooltip: getSharedTooltip(scatterPlotTooltipFormatter),
    xAxis: {
      title: { text: formatMessage('RiskRewardPlot.AxisLabels.X') },
      floor: 0,
      ...(axes ? axes.x : {}),
    },
    yAxis: {
      title: { text: formatMessage('RiskRewardPlot.AxisLabels.Y') },
      ...(axes ? axes.y : {}),
    },
  });
}

export function formatRiskRewardItemsForHistoricalScenarios({
  chartItems,
  showBaseCurrencies,
  localCurrency,
}: {
  chartItems: any[];
  showBaseCurrencies?: boolean;
  localCurrency: string;
}): ScatterPlotDatum[] {
  return chartItems.reduce((acc: ScatterPlotDatum[], item) => {
    const symbol = item.type === COMPARISON_ITEM_TYPES.BENCHMARK ? 'diamond' : 'circle';
    const itemName = formatLocalizedString(item.name);

    const stDev = item.metrics?.standardDeviation;
    const annualizedReturns = item.metrics?.annualizedReturns;
    const fxSuffix = showBaseCurrencies && item.currency && item.currency !== CURRENCY.CAD ? formatMessage('Charts.CADConverted') : '';
    // there is no name suffix for benchmarks
    // fx uses the cadConverted suffix (if it's showing base currency and the item isn't CAD)
    // if not fx but showing base currency then suffix is item currency
    // otherwise no suffix
    const nameSuffix = COMPARISON_ITEM_TYPES.BENCHMARK ? '' : item.isFX ? fxSuffix : showBaseCurrencies ? currencyToText(item.currency) : '';

    if (stDev != null && annualizedReturns != null) {
      const key = item._id;

      acc.push({
        key,
        type: 'scatter',
        data: [
          {
            key,
            x: stDev,
            y: annualizedReturns,
            name: itemName,
            color: item.color,
            currency: localCurrency,
            nameSuffix,
            marker: {
              symbol,
              radius: item.isSubHolding ? 4 : 6,
            },
          },
        ],
      });
    }

    return acc;
  }, []);
}

export function formatRiskRewardItems(
  chartItems: any[],
  monthsRange: number,
  showBaseCurrencies: boolean,
  localCurrency: string,
  showItemBenchmarks: boolean = false,
): ScatterPlotDatum[] {
  const period = monthsToPeriod(monthsRange);

  return chartItems.reduce((acc: ScatterPlotDatum[], item) => {
    const symbol = item.type === COMPARISON_ITEM_TYPES.BENCHMARK ? 'diamond' : 'circle';
    const itemName = formatLocalizedString(item.name);

    // Local currency or FX
    if (item.isFX || !showBaseCurrencies) {
      const stDev = item.performance?.standardDeviation?.[period];
      const periodReturn = item.performance?.percentGrowth?.[period];

      if (stDev != null && periodReturn != null) {
        const key = `${item._id}-${localCurrency}`;

        acc.push({
          key,
          type: 'scatter',
          data: [
            {
              key,
              x: stDev,
              y: periodReturn,
              name: itemName,
              color: item.color,
              currency: localCurrency,
              nameSuffix: showBaseCurrencies && item.currency && item.currency !== CURRENCY.CAD ? formatMessage('Charts.CADConverted') : '',
              marker: {
                symbol,
                radius: item.isSubHolding ? 4 : 6,
              },
            },
          ],
        });
      }
    }

    // Base currency
    if (showBaseCurrencies) {
      const performance = item.performanceBase;
      const baseStDev = performance?.standardDeviation?.[period];
      const basePeriodReturn = performance?.percentGrowth?.[period];

      if (baseStDev != null && basePeriodReturn != null) {
        const key = `${item._id}-BASE`;

        acc.push({
          key,
          type: 'scatter',
          data: [
            {
              key,
              x: baseStDev,
              y: basePeriodReturn,
              name: itemName,
              color: item.color,
              currency: item.currency,
              nameSuffix: `(${currencyToText(item.currency)})`,
              marker: {
                symbol,
                radius: item.isSubHolding ? 4 : 6,
              },
            },
          ],
        });
      }
    }

    // Benchmarks
    if (showItemBenchmarks && item.benchmarks && item.benchmarks.market && item.benchmarks.market[item._id]) {
      const benchmark = item.benchmarks.market[item._id];
      const benchmarkStDev = benchmark.performance.standardDeviation ? benchmark.performance.standardDeviation[period] : null;
      const benchmarkPeriodReturn = benchmark.performance.percentGrowth ? benchmark.performance.percentGrowth[period] : null;

      if (benchmarkStDev != null && benchmarkPeriodReturn != null) {
        acc.push({
          type: 'scatter',
          data: [
            {
              key: benchmark.lipperId,
              x: benchmarkStDev,
              y: benchmarkPeriodReturn,
              name: formatLocalizedString(benchmark.names.full),
              color: item.color,
              marker: {
                symbol: 'diamond',
              },
            },
          ],
        });
      }
    }

    return acc;
  }, []);
}

export const getDisclaimerDates = (items: ComparisonItem[]) => {
  const itemDates = getLatestDates(items);
  return {
    risk: itemDates.risk,
    performance: itemDates.performance,
  };
};
